import { ListSkeleton, Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@atlas/ui-components'
import { Badge, Button } from '@atlas/ui-primitives'
import type { WorkflowDTO, WorkflowResolverRuleDTO } from '@/domain/workflow-admin.types'
import { EmptyState, ErrorState } from './states'

/**
 * The tenant's resolver rules in priority order, plus a read-only fallback row
 * rendered last. The fallback stands for the tenant default workflow, which is
 * what resolution uses when no rule matches — it is not a persisted rule, holds
 * no priority, and therefore carries no edit, delete, or move controls.
 */

export interface ResolverRuleListProps {
	rules: WorkflowResolverRuleDTO[]
	/** Every workflow, for naming a rule's target. */
	workflows: WorkflowDTO[]
	isLoading?: boolean
	error?: unknown
	onRetry?: () => void
	onMove: (ruleId: string, direction: 'up' | 'down') => void
	onEdit?: (ruleId: string) => void
	onDelete?: (ruleId: string) => void
	/** True while a reorder or rule mutation is in flight — move controls disable. */
	isMutating?: boolean
}

const matcherText = (values: string[] | null) => (values && values.length > 0 ? values.join(', ') : 'any')

function FallbackRow({ defaultWorkflow }: { defaultWorkflow: WorkflowDTO | undefined }) {
	return (
		<TableRow data-testid="resolver-fallback-row" className="bg-ds-surface">
			<TableCell>
				<Badge variant="muted" size="sm">
					fallback
				</Badge>
			</TableCell>
			<TableCell>any</TableCell>
			<TableCell>any</TableCell>
			<TableCell>{defaultWorkflow ? defaultWorkflow.name : 'No default workflow configured'}</TableCell>
			<TableCell className="text-ds-text-muted text-sm">Tenant default — used when no rule matches</TableCell>
		</TableRow>
	)
}

export function ResolverRuleList(props: ResolverRuleListProps) {
	const { rules, workflows, isLoading, error, onRetry, onMove, onEdit, onDelete, isMutating } = props
	const defaultWorkflow = workflows.find((workflow) => workflow.isDefault && workflow.isActive && !workflow.deletedAt)

	if (isLoading) return <ListSkeleton label="Loading resolver rules" />
	if (error) return <ErrorState error={error} onRetry={onRetry} title="Could not load resolver rules" />

	const ordered = [...rules].sort((a, b) => a.priority - b.priority)
	// Ordering is defined over the active rule set only: an inactive rule is listed
	// for administration but holds no place in that order, so it carries no move controls.
	const activeOrder = ordered.filter((rule) => rule.isActive).map((rule) => rule.ruleId)
	const nameOf = (workflowId: string) =>
		workflows.find((workflow) => workflow.workflowId === workflowId)?.name ?? workflowId

	const table = (
		<Table>
			<TableHeader>
				<TableRow>
					<TableHead>Priority</TableHead>
					<TableHead>Asset types</TableHead>
					<TableHead>Complexity</TableHead>
					<TableHead>Target workflow</TableHead>
					<TableHead>Actions</TableHead>
				</TableRow>
			</TableHeader>
			<TableBody>
				{ordered.map((rule) => {
					const activeIndex = activeOrder.indexOf(rule.ruleId)
					return (
						<TableRow key={rule.ruleId} data-testid={`resolver-rule-${rule.ruleId}`}>
							<TableCell>{rule.priority}</TableCell>
							<TableCell>{matcherText(rule.assetTypeCodes)}</TableCell>
							<TableCell>{matcherText(rule.complexityLevels)}</TableCell>
							<TableCell>{nameOf(rule.workflowId)}</TableCell>
							<TableCell className="flex gap-2">
								{activeIndex !== -1 && (
									<>
										<Button
											variant="outline"
											isDisabled={activeIndex === 0 || isMutating}
											onPress={() => onMove(rule.ruleId, 'up')}
											aria-label={`Move ${rule.ruleId} up`}
										>
											↑
										</Button>
										<Button
											variant="outline"
											isDisabled={activeIndex === activeOrder.length - 1 || isMutating}
											onPress={() => onMove(rule.ruleId, 'down')}
											aria-label={`Move ${rule.ruleId} down`}
										>
											↓
										</Button>
									</>
								)}
								{onEdit && (
									<Button variant="outline" onPress={() => onEdit(rule.ruleId)} aria-label={`Edit ${rule.ruleId}`}>
										Edit
									</Button>
								)}
								{onDelete && (
									<Button variant="outline" onPress={() => onDelete(rule.ruleId)} aria-label={`Delete ${rule.ruleId}`}>
										Delete
									</Button>
								)}
							</TableCell>
						</TableRow>
					)
				})}
				<FallbackRow defaultWorkflow={defaultWorkflow} />
			</TableBody>
		</Table>
	)

	if (ordered.length === 0) {
		return (
			<div className="space-y-4">
				<EmptyState
					title="No resolver rules"
					description="Every new Work Order resolves to the tenant default workflow shown below."
				/>
				{table}
			</div>
		)
	}
	return table
}
